import { useCallback, useEffect, useRef } from 'react';

import { CRM_RequestType, LeadIntent } from '@mewa/types';

import {
  clearLeadIntent,
  clearServiceSegment,
  loadFormState,
  loadLeadIntent,
  loadServiceSegment,
} from '../../persistence';
import { useFormData } from './use-form-data';

export type FormState = ReturnType<typeof useFormState>;

export const useFormState = (formType: CRM_RequestType) => {
  const didMount = useRef(false);
  const formDataState = useFormData(formType);

  const maxStep = useCallback(() => {
    if (!formDataState.isValid()) {
      return 0;
    }

    return 1;
  }, [formDataState]);

  useEffect(() => {
    if (didMount.current) {
      return;
    }

    didMount.current = true;

    const initialState = loadFormState();

    // set service segment only if form is consultation
    if (formType === CRM_RequestType.Consultation) {
      const storedServiceSegment = loadServiceSegment();
      clearServiceSegment();
      const storeLeadIntent = loadLeadIntent();
      clearLeadIntent();

      // use service segment passed from previous page
      if (storedServiceSegment !== null) {
        formDataState.serviceSegment.changeValue(storedServiceSegment);
      } else if (initialState) {
        formDataState.serviceSegment.changeValue(
          initialState.formDataState.serviceSegment,
        );
      }

      // use lead intent passed from previous page
      if (storeLeadIntent !== null) {
        formDataState.leadIntent.changeValue(storeLeadIntent);
      } else if (initialState) {
        formDataState.leadIntent.changeValue(
          initialState.formDataState.leadIntent,
        );
      }
    }

    // Set default values if form is service test
    if (formType === CRM_RequestType.ServiceTest) {
      formDataState.leadIntent.changeValue(LeadIntent.product);
    }

    // Set default values if form is brochures download
    if (formType === CRM_RequestType.OrderService) {
      formDataState.leadIntent.changeValue(LeadIntent.product);
    }

    if (initialState) {
      formDataState.firstName.changeValue(initialState.formDataState.firstName);
      formDataState.lastName.changeValue(initialState.formDataState.lastName);
      formDataState.email.changeValue(initialState.formDataState.email);
      formDataState.phone.changeValue(initialState.formDataState.phone);
      formDataState.message.changeValue(initialState.formDataState.message);
      formDataState.responsePreference.changeValue(
        initialState.formDataState.responsePreference,
      );
    }
  }, [formDataState, formType]);

  return {
    formDataState,
    maxStep: maxStep(),
    isValid: formDataState.isValid,
    triggerValidation: formDataState.triggerValidation,
  };
};
